// constants
import Constants from '@/static/constants/constants.json';
import { WorkflowLevel, UnifiedTabContext, VendorStatus } from '@/types/UnifiedTab.types';

// Unified workflow level configuration
export const WORKFLOW_LEVELS: {
  prePanel: WorkflowLevel;
  businessReview: WorkflowLevel;
  tastingPanel: WorkflowLevel;
} = {
  prePanel: {
    level: 1,
    name: 'Pre-Panel',
    activeStatuses: [
      'product_development',
      'vendor_assessment',
      'prepare_for_panel',
      'business_review_panel',
      'tasting_panel'
    ],
    previousStateStatuses: [
      'product_development',
      'vendor_assessment', 
      'prepare_for_panel',
      'business_review_panel',
      'tasting_panel'
    ],
    includesLowerLevels: true
  },
  businessReview: {
    level: 2,
    name: 'Business Review',
    activeStatuses: [
      'business_review_panel',
      'tasting_panel'
    ],
    previousStateStatuses: [
      'business_review_panel',
      'tasting_panel'
    ],
    includesLowerLevels: true
  },
  tastingPanel: {
    level: 3,
    name: 'Tasting Panel',
    activeStatuses: [
      'tasting_panel'
    ],
    previousStateStatuses: [
      'tasting_panel'
    ],
    includesLowerLevels: false
  }
};

// Path-based tab configuration
export const PATH_TAB_CONFIG = {
  prePanelPaths: ['prepare-for-panel', 'business-review-panel', 'tasting-panel', 'shopping-list-management'],
  businessReviewPaths: ['business-review-panel', 'tasting-panel', 'shopping-list-management'],
  tastingPanelPaths: ['tasting-panel', 'shopping-list-management']
};

// Helper functions
export const isArchivedOrOnHold = (vendorStatus: string): boolean => {
  const notApplicableStatuses = [
    Constants.ARCHIVED_STATUS.toLowerCase(),
    Constants.ON_HOLD_STATUS.toLowerCase()
  ];
  return notApplicableStatuses.includes(vendorStatus.toLowerCase());
};

export const getVendorStatusType = (vendorStatus: string): VendorStatus => {
  if (vendorStatus.toLowerCase() === Constants.ARCHIVED_STATUS.toLowerCase()) {
    return 'archived';
  }
  if (vendorStatus.toLowerCase() === Constants.ON_HOLD_STATUS.toLowerCase()) {
    return 'on_hold';
  }
  if (vendorStatus.toLowerCase() === Constants.CANCELED_STATUS.toLowerCase()) {
    return 'cancelled';
  }
  return 'active';
};

export const getWorkflowLevelFromStatus = (
  vendorStatus: string, 
  previousState: string | null,
  isArchivedOrOnHold: boolean
): number => {
  if (isArchivedOrOnHold && previousState) {
    // For archived/on-hold vendors, use previous state
    const status = previousState.toLowerCase();
    if (WORKFLOW_LEVELS.tastingPanel.previousStateStatuses.includes(status)) {
      return WORKFLOW_LEVELS.tastingPanel.level;
    }
    if (WORKFLOW_LEVELS.businessReview.previousStateStatuses.includes(status)) {
      return WORKFLOW_LEVELS.businessReview.level;
    }
    if (WORKFLOW_LEVELS.prePanel.previousStateStatuses.includes(status)) {
      return WORKFLOW_LEVELS.prePanel.level;
    }
  } else {
    // For active vendors, use current status
    const status = vendorStatus.toLowerCase();
    if (WORKFLOW_LEVELS.tastingPanel.activeStatuses.includes(status)) {
      return WORKFLOW_LEVELS.tastingPanel.level;
    }
    if (WORKFLOW_LEVELS.businessReview.activeStatuses.includes(status)) {
      return WORKFLOW_LEVELS.businessReview.level;
    }
    if (WORKFLOW_LEVELS.prePanel.activeStatuses.includes(status)) {
      return WORKFLOW_LEVELS.prePanel.level;
    }
  }
  
  return 0; // Default level
};

export const shouldEnableTabForWorkflowLevel = (
  tabLevel: number,
  vendorWorkflowLevel: number,
  includesLowerLevels: boolean
): boolean => {
  if (includesLowerLevels) {
    return vendorWorkflowLevel >= tabLevel;
  }
  return vendorWorkflowLevel === tabLevel;
};

export const hasBusinessReviewAccess = (context: UnifiedTabContext): boolean => {
  // Business review special logic - only for specific paths and workflow levels
  const businessReviewPaths = ['business-review-panel', 'tasting-panel', 'shopping-list-management'];
  const hasPathAccess = businessReviewPaths.some(path => context.pathname.includes(path));
  const hasWorkflowAccess = context.currentWorkflowLevel >= WORKFLOW_LEVELS.businessReview.level;
  
  return hasPathAccess && hasWorkflowAccess;
};

export const isPathAllowed = (pathname: string, allowedPaths: string[]): boolean => {
  return allowedPaths.some(path => pathname.includes(path));
};
